import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Loader2, CheckCircle2, AlertTriangle, KeyRound, Server, Activity, X } from "lucide-react";

// Admin > CML tab — manage CML credentials + view live sessions + tune the
// node-slot budget. Visible only to the admin user (server enforces).
export default function CMLTab({ adminEmail }) {
  const [creds, setCreds] = useState(null);                  // null=loading, false=none, dict=have
  const [budget, setBudget] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const reload = async () => {
    setLoading(true);
    try {
      const [c, b, s, h] = await Promise.all([
        api.get("/admin/cml/credentials").then((r) => r.data).catch(() => ({ configured: false })),
        api.get("/admin/cml/budget").then((r) => r.data).catch(() => null),
        api.get("/admin/cml/sessions").then((r) => r.data.sessions).catch(() => []),
        api.get("/admin/cml/health").then((r) => r.data).catch(() => null),
      ]);
      setCreds(c.configured ? c : false);
      setBudget(b);
      setSessions(s);
      setHealth(h);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  if (loading) {
    return (
      <div className="flex items-center gap-3 text-zinc-500 py-12 justify-center">
        <Loader2 className="animate-spin" size={16} /> Loading CML state…
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="cml-admin-tab">
      {/* Connection status pill */}
      <CMLStatusCard health={health} onTest={async () => {
        try {
          const { data } = await api.post("/admin/cml/credentials/test");
          toast.success(`CML ${data.version || ""} reachable (${data.rtt_ms}ms)`);
          reload();
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Test failed");
        }
      }} />

      {/* Credentials */}
      <CMLCredsCard creds={creds} onSaved={reload} />

      {/* Budget */}
      <CMLBudgetCard budget={budget} onSaved={reload} />

      {/* Live sessions */}
      <CMLSessionsCard sessions={sessions} onForceEnd={async (sid) => {
        if (!window.confirm(`Force-end CML session ${sid}? This will tear down the CML lab and free the slot.`)) return;
        try {
          await api.post(`/admin/cml/sessions/${sid}/force-end`);
          toast.success("Session ended");
          reload();
        } catch (e) {
          toast.error(e?.response?.data?.detail || "Force-end failed");
        }
      }} />
    </div>
  );
}

function CMLStatusCard({ health, onTest }) {
  const ok = health?.ok;
  const configured = health?.configured;
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-5" data-testid="cml-status-card">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-mono">CML connection</div>
        <button onClick={onTest} className="text-xs px-3 py-1 rounded border border-zinc-700 hover:border-[#00E5FF] hover:text-[#00E5FF] transition" data-testid="cml-test-btn">
          Test connection
        </button>
      </div>
      {!configured && (
        <div className="flex items-center gap-2 text-amber-400 text-sm">
          <AlertTriangle size={16} /> Not configured — save credentials below.
        </div>
      )}
      {configured && ok && (
        <div className="space-y-1 text-sm">
          <div className="flex items-center gap-2 text-emerald-400">
            <CheckCircle2 size={16} /> Reachable
          </div>
          <div className="text-zinc-400 font-mono text-xs">
            endpoint: <span className="text-zinc-200">{health.endpoint}</span>
          </div>
          <div className="text-zinc-400 font-mono text-xs">
            version: <span className="text-zinc-200">{health.version}</span> · rtt {health.rtt_ms}ms · slots {health.slots_used}/{health.slots_total}
          </div>
        </div>
      )}
      {configured && !ok && (
        <div className="flex items-center gap-2 text-rose-400 text-sm">
          <AlertTriangle size={16} /> {health.message || "Unreachable"}
        </div>
      )}
    </div>
  );
}

function CMLCredsCard({ creds, onSaved }) {
  const [editing, setEditing] = useState(!creds);
  const [form, setForm] = useState({
    endpoint: creds?.endpoint || "https://cml.hypexlabs.com",
    api_username: creds?.api_username || "",
    api_password: "",
    verify_tls: creds?.verify_tls || false,
  });
  const [saving, setSaving] = useState(false);

  if (!editing) {
    return (
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-5" data-testid="cml-creds-saved">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-mono mb-2">credentials</div>
        <div className="text-sm space-y-1">
          <div className="font-mono text-xs text-zinc-400">endpoint: <span className="text-zinc-200">{creds.endpoint}</span></div>
          <div className="font-mono text-xs text-zinc-400">api_username: <span className="text-zinc-200">{creds.api_username}</span></div>
          <div className="font-mono text-xs text-zinc-400">verify_tls: <span className="text-zinc-200">{String(creds.verify_tls)}</span></div>
          <div className="font-mono text-xs text-zinc-500">updated: {creds.updated_at} by {creds.updated_by}</div>
        </div>
        <button onClick={() => setEditing(true)} className="mt-3 text-xs px-3 py-1 rounded border border-zinc-700 hover:border-[#00E5FF] hover:text-[#00E5FF] transition" data-testid="cml-creds-edit-btn">
          Replace credentials
        </button>
      </div>
    );
  }

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/admin/cml/credentials", form);
      toast.success("CML credentials saved");
      setEditing(false);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-5 space-y-3" data-testid="cml-creds-form">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-zinc-500 font-mono">
        <KeyRound size={12} /> credentials
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="text-xs text-zinc-400">
          Endpoint
          <input type="text" value={form.endpoint} onChange={(e) => setForm({ ...form, endpoint: e.target.value })}
                  required placeholder="https://cml.hypexlabs.com"
                  className="mt-1 w-full px-3 py-2 rounded bg-zinc-950 border border-zinc-800 text-sm text-zinc-100"
                  data-testid="cml-endpoint-input" />
        </label>
        <label className="text-xs text-zinc-400">
          API username
          <input type="text" value={form.api_username} onChange={(e) => setForm({ ...form, api_username: e.target.value })}
                  required className="mt-1 w-full px-3 py-2 rounded bg-zinc-950 border border-zinc-800 text-sm text-zinc-100"
                  data-testid="cml-username-input" />
        </label>
        <label className="text-xs text-zinc-400 sm:col-span-2">
          API password
          <input type="password" value={form.api_password} onChange={(e) => setForm({ ...form, api_password: e.target.value })}
                  required placeholder="stored Fernet-encrypted"
                  className="mt-1 w-full px-3 py-2 rounded bg-zinc-950 border border-zinc-800 text-sm text-zinc-100"
                  data-testid="cml-password-input" />
        </label>
        <label className="flex items-center gap-2 text-xs text-zinc-400 sm:col-span-2">
          <input type="checkbox" checked={form.verify_tls} onChange={(e) => setForm({ ...form, verify_tls: e.target.checked })}
                  data-testid="cml-verify-tls-checkbox" />
          Verify TLS cert (turn on once cml.hypexlabs.com has a valid Let's Encrypt cert)
        </label>
      </div>
      <div className="flex items-center gap-2 pt-1">
        <button type="submit" disabled={saving}
                className="text-sm px-4 py-1.5 rounded bg-[#00E5FF] text-zinc-950 font-medium hover:bg-cyan-300 transition disabled:opacity-50"
                data-testid="cml-creds-save-btn">
          {saving ? <Loader2 className="animate-spin inline" size={14} /> : "Save credentials"}
        </button>
        {creds && (
          <button type="button" onClick={() => setEditing(false)}
                  className="text-xs text-zinc-400 hover:text-zinc-100">Cancel</button>
        )}
      </div>
    </form>
  );
}

function CMLBudgetCard({ budget, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(budget?.total_node_slots ?? 40);
  if (!budget) return null;

  const save = async () => {
    try {
      await api.put("/admin/cml/budget", { total_node_slots: Number(val) });
      toast.success(`Slot budget updated to ${val}`);
      setEditing(false);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const pct = Math.round((budget.used_node_slots / budget.total_node_slots) * 100);
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-5" data-testid="cml-budget-card">
      <div className="flex items-center justify-between mb-3">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-mono flex items-center gap-2">
          <Server size={12} /> node-slot budget
        </div>
        {!editing && (
          <button onClick={() => { setVal(budget.total_node_slots); setEditing(true); }}
                  className="text-xs px-3 py-1 rounded border border-zinc-700 hover:border-[#00E5FF] hover:text-[#00E5FF] transition">
            Adjust
          </button>
        )}
      </div>
      <div className="text-2xl font-mono text-zinc-100" data-testid="cml-budget-utilization">
        {budget.used_node_slots} <span className="text-zinc-600">/</span> {budget.total_node_slots}
      </div>
      <div className="mt-2 h-1.5 bg-zinc-800 rounded overflow-hidden">
        <div className="h-full bg-[#00E5FF]" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-2 text-[11px] text-zinc-500 font-mono">{pct}% used · avg session {Math.round(budget.avg_session_seconds / 60)}min</div>
      {editing && (
        <div className="mt-3 flex items-center gap-2">
          <input type="number" min={1} value={val} onChange={(e) => setVal(e.target.value)}
                  className="w-24 px-2 py-1 rounded bg-zinc-950 border border-zinc-800 text-sm text-zinc-100" />
          <button onClick={save} className="text-xs px-3 py-1 rounded bg-[#00E5FF] text-zinc-950 hover:bg-cyan-300">Save</button>
          <button onClick={() => setEditing(false)} className="text-xs text-zinc-400">Cancel</button>
        </div>
      )}
    </div>
  );
}

function CMLSessionsCard({ sessions, onForceEnd }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-5" data-testid="cml-sessions-card">
      <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-mono mb-3 flex items-center gap-2">
        <Activity size={12} /> sessions ({sessions.length})
      </div>
      {sessions.length === 0 ? (
        <div className="text-zinc-500 text-sm py-2">No CML sessions yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-mono">
            <thead>
              <tr className="text-zinc-500 border-b border-zinc-800">
                <th className="text-left py-2 pr-3">id</th>
                <th className="text-left py-2 pr-3">lab</th>
                <th className="text-left py-2 pr-3">status</th>
                <th className="text-left py-2 pr-3">nodes</th>
                <th className="text-left py-2 pr-3">boot</th>
                <th className="text-left py-2 pr-3"></th>
              </tr>
            </thead>
            <tbody>
              {sessions.map((s) => (
                <tr key={s.session_id} className="border-b border-zinc-900">
                  <td className="py-2 pr-3 text-zinc-300">{s.session_id.slice(0, 10)}…</td>
                  <td className="py-2 pr-3 text-zinc-300">{s.lab_id}</td>
                  <td className="py-2 pr-3">
                    <StatusPill status={s.status} />
                  </td>
                  <td className="py-2 pr-3 text-zinc-400">{s.node_count}</td>
                  <td className="py-2 pr-3 text-zinc-400">{s.boot_seconds ? `${s.boot_seconds}s` : "—"}</td>
                  <td className="py-2 pr-3">
                    {["ready", "booting", "grading", "provisioning"].includes(s.status) && (
                      <button onClick={() => onForceEnd(s.session_id)}
                              className="text-rose-400 hover:text-rose-300 inline-flex items-center gap-1"
                              data-testid={`cml-force-end-${s.session_id}`}>
                        <X size={12} /> end
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function StatusPill({ status }) {
  const colors = {
    queued: "text-amber-400 bg-amber-950/50",
    provisioning: "text-cyan-400 bg-cyan-950/50",
    booting: "text-cyan-400 bg-cyan-950/50",
    ready: "text-emerald-400 bg-emerald-950/50",
    grading: "text-violet-400 bg-violet-950/50",
    tearing_down: "text-zinc-400 bg-zinc-950/50",
    ended: "text-zinc-500 bg-zinc-950/50",
    error: "text-rose-400 bg-rose-950/50",
  };
  return (
    <span className={`px-2 py-0.5 rounded ${colors[status] || "text-zinc-400 bg-zinc-950"}`}>
      {status}
    </span>
  );
}
